import { Box, Button, Grid, Typography } from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import CustomInput from '../Returns/CustomInput'
import CustomMultiSelect from '../Returns/CustomMultiSelect'
import calendarIcon from '../../assets/calendar.svg'
import {
  MIN_COMPLIANCE_PERIOD_DATE,
  getFinancialYearForDate,
} from '../../utils/complianceMasterUtils'

const CalendarIcon = props => (
  <img src={calendarIcon} alt="" width={20} height={20} {...props} />
)

const FieldLabel = ({ children, required = false }) => (
  <Typography
    sx={{
      fontSize: '16px',
      fontWeight: 500,
      color: 'var(--forbrand-primary)',
      mb: 1,
      lineHeight: '140%',
    }}
  >
    {children}
    {required && <span style={{ color: 'var(--forbrand-error)' }}>*</span>}
  </Typography>
)

const datePickerSlotProps = ({ error, helperText, disabled } = {}) => ({
  textField: {
    fullWidth: true,
    error,
    helperText,
    sx: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
        fontSize: '14px',
        height: '48px',
        borderRadius: 'unset',
        '& fieldset': {
          borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
        },
        '&:hover fieldset': {
          borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
        },
        '&.Mui-focused fieldset': {
          borderColor: error ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
        },
        '&.Mui-disabled': {
          backgroundColor: 'var(--bg-disabled)',
        },
      },
      '& .MuiInputBase-input': {
        fontSize: '14px',
        color: '#374151',
      },
    },
  },
})

const ComplianceMasterForm = ({
  mode = 'add',
  values,
  entities = [],
  tanList = [],
  categories = [],
  formErrors = {},
  formLoading = false,
  onFieldChange,
  onSubmit,
}) => {
  const isEdit = mode === 'edit'

  // Get available TANs based on the currently selected Entity Names
  const getAvailableTANs = selectedEntityNames => {
    if (!selectedEntityNames || selectedEntityNames.length === 0) {
      return []
    }
    if (!tanList || tanList.length === 0) {
      return []
    }

    const availableTANs = tanList
      .filter(tanItem => selectedEntityNames.includes(tanItem.entityName))
      .map(tanItem => tanItem.tan)

    return [...new Set(availableTANs)]
  }

  const availableTANs = getAvailableTANs(values.entityNames)

  const compliancePeriodDate = values.compliancePeriodDate
    ? dayjs(values.compliancePeriodDate, 'DD/MM/YYYY')
    : null
  const dueDateValue = values.dueDate ? dayjs(values.dueDate, 'DD/MM/YYYY') : null
  const completionDateValue = values.completionDate
    ? dayjs(values.completionDate, 'DD/MM/YYYY')
    : null

  const handleCompliancePeriodChange = newDate => {
    if (!newDate) return
    const jsDate = newDate.toDate()
    const derivedFY = getFinancialYearForDate(jsDate)
    onFieldChange('compliancePeriodDate', newDate.format('DD/MM/YYYY'))
    onFieldChange('compliancePeriod', derivedFY)
  }

  const handleCompletionDateChange = newDate => {
    onFieldChange('completionDate', newDate ? newDate.format('DD/MM/YYYY') : '')
  }

  // In edit mode, disabled fields should not look greyed out: override the
  // shared `--bg-disabled` variable to white and keep disabled text readable,
  // scoped to this form only.
  const editModeSx = isEdit
    ? {
        '--bg-disabled': '#ffffff',
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#374151',
        },
        '& .MuiSelect-select.Mui-disabled': {
          WebkitTextFillColor: '#374151',
        },
      }
    : {}

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={editModeSx}>
        {/* Mandatory fields note (add mode only) */}
        {!isEdit && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--forbrand-error)',
              mb: 3,
              fontSize: '14px',
            }}
          >
            (*) Fields are mandatory
          </Typography>
        )}

        {/* Form container */}
        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* Compliance Category */}
            <Grid item size={6}>
              <CustomMultiSelect
                label="Compliance Category"
                fullWidth
                required
                placeholder="Select Compliance Category"
                value={values.complianceCategories || []}
                onChange={e => onFieldChange('complianceCategories', e.target.value)}
                options={categories}
                getOptionLabel={category => category.name}
                getOptionValue={category => category.name}
                disabled={isEdit}
                error={!!formErrors.complianceCategories}
                helperText={formErrors.complianceCategories}
              />
            </Grid>

            {/* Entity Name */}
            <Grid item size={6}>
              <CustomMultiSelect
                label="Entity Name"
                fullWidth
                required
                placeholder="Select Entity Name"
                value={values.entityNames || []}
                onChange={e => onFieldChange('entityNames', e.target.value)}
                options={entities}
                getOptionLabel={entity => entity.name}
                getOptionValue={entity => entity.name}
                disabled={isEdit}
                error={!!formErrors.entityNames}
                helperText={formErrors.entityNames}
              />
            </Grid>

            {/* Entity TAN */}
            <Grid item size={6}>
              <CustomMultiSelect
                label="Entity TAN"
                fullWidth
                required
                placeholder="Select Entity TAN"
                value={values.entityTANs || []}
                onChange={e => onFieldChange('entityTANs', e.target.value)}
                options={availableTANs}
                disabled={
                  isEdit || !values.entityNames || values.entityNames.length === 0
                }
                error={!!formErrors.entityTANs}
                helperText={formErrors.entityTANs}
              />
            </Grid>

            {/* Start Date (calendar; editable in add mode, disabled in edit mode) */}
            <Grid item size={6}>
              <FieldLabel required>Start Date</FieldLabel>
              <DatePicker
                value={compliancePeriodDate}
                onChange={handleCompliancePeriodChange}
                format="DD/MM/YYYY"
                minDate={dayjs(MIN_COMPLIANCE_PERIOD_DATE)}
                maxDate={dayjs()}
                disabled={isEdit}
                slots={{ openPickerIcon: CalendarIcon }}
                slotProps={datePickerSlotProps({
                  error: !!formErrors.compliancePeriod,
                  helperText: formErrors.compliancePeriod,
                  disabled: isEdit,
                })}
              />
            </Grid>

            {/* Due Date (calendar, disabled) */}
            {/* <Grid item size={6}>
              <FieldLabel>Due Date</FieldLabel>
              <DatePicker
                value={dueDateValue}
                format="DD/MM/YYYY"
                disabled
                slots={{ openPickerIcon: CalendarIcon }}
                slotProps={datePickerSlotProps({ disabled: true })}
              />
            </Grid> */}

            {/* Edit-only fields: Completion Date (editable) then Status (disabled) */}
            {isEdit && (
              <>
                <Grid item size={6}>
                  <FieldLabel>Completion Date</FieldLabel>
                  <DatePicker
                    value={completionDateValue}
                    onChange={handleCompletionDateChange}
                    format="DD/MM/YYYY"
                    slots={{ openPickerIcon: CalendarIcon }}
                    slotProps={datePickerSlotProps({
                      error: !!formErrors.completionDate,
                      helperText: formErrors.completionDate,
                    })}
                  />
                </Grid>

                <Grid item size={6}>
                  <CustomInput
                    label="Status"
                    fullWidth
                    placeholder="Status"
                    value={values.status || ''}
                    disabled
                  />
                </Grid>
              </>
            )}
          </Grid>

          {formErrors.general && (
            <Typography variant="body2" color="error" sx={{ my: 2 }}>
              {formErrors.general}
            </Typography>
          )}
        </Box>

        {/* Submit button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading}
            onClick={onSubmit}
            sx={{
              backgroundColor: 'var( --forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: '16px',
              py: '8px',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: '4px',
              '&:disabled': {
                backgroundColor: '#ccc',
                boxShadow: 'none',
              },
            }}
          >
            {formLoading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}

export default ComplianceMasterForm
